<template>
  <v-form
    ref="formRef"
    class="free-generator-form method-form"
    @submit.prevent=""
  >
    <!-- Primäre Energieerzeugung -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">
          mdi-lightning-bolt-circle
        </v-icon>
        <span>Primäre Energieerzeugung</span>
      </div>

      <!-- Primary Effort Factor -->
      <v-text-field
        v-model.number="form.mainEffortFactor"
        type="number"
        :error-messages="validationErrors?.main_effort_factor"
        label="Primäre Aufwandszahl"
        :hint="hint.main_effort_factor"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip
            v-if="hint.main_effort_factor"
            location="bottom"
            color="surface" 
            content-class="tooltip"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.main_effort_factor }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.main_effort_factor"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für primäre Aufwandszahl übernehmen"
            @click="copyFromReference('main_effort_factor')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Fuel -->
      <v-autocomplete
        v-model="form.fuel"
        :error-messages="validationErrors?.fuel"
        label="Primärer Energieträger"
        :items="fuels"
        item-title="name"
        item-value="id"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.fuel" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.fuel }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.fuel"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für primären Energieträger übernehmen"
            @click="copyFromReference('fuel')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- Passiv erzeugte Energie -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">
          mdi-leaf-circle-outline
        </v-icon>
        <span>Passiv erzeugte Energie</span>
      </div>

      <!-- Passive Generated Energy -->
      <v-autocomplete
        v-model="form.passiveGeneratedEnergy"
        :error-messages="validationErrors?.passive_generated_energy"
        label="Passiv erzeugte Energieform"
        :items="energies"
        item-title="name"
        item-value="id"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip
            v-if="hint.passive_generated_energy"
            location="bottom"
            color="surface" 
            content-class="tooltip"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.passive_generated_energy }}</span>
          </v-tooltip>

          <v-btn
            v-if="
              !reference &&
              referenceData?.passive_generated_energy
            "
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für passiv erzeugte Energieform übernehmen"
            @click="copyFromReference('passive_generated_energy')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Passive Effort Factor -->
      <v-text-field
        v-if="form.passiveGeneratedEnergy"
        v-model.number="form.passiveEffortFactor"
        type="number"
        :error-messages="validationErrors?.passive_effort_factor"
        label="Passive Aufwandszahl"
        :hint="hint.passive_effort_factor"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip
            v-if="hint.passive_effort_factor"
            location="bottom"
            color="surface" 
            content-class="tooltip"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.passive_effort_factor }}</span>
          </v-tooltip>

          <v-btn
            v-if="
              !reference &&
              referenceData?.passive_effort_factor
            "
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für passive Aufwandszahl übernehmen"
            @click="copyFromReference('passive_effort_factor')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Hilfsenergie -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">
          mdi-transmission-tower
        </v-icon>
        <span>Hilfsenergie</span>
      </div>

      <!-- Supply Energy Friction -->
      <v-text-field
        v-model.number="form.supplyEnergyFriction"
        type="number"
        :error-messages="validationErrors?.supply_energy_friction"
        label="Anteil elektrischer Hilfsenergie"
        suffix="%"
        :hint="hint.supply_energy_friction"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip
            v-if="hint.supply_energy_friction"
            location="bottom"
            color="surface" 
            content-class="tooltip"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.supply_energy_friction }}</span>
          </v-tooltip>

          <v-btn
            v-if="
              !reference &&
              referenceData?.supply_energy_friction
            "
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für elektrische Hilfsenergie übernehmen"
            @click="copyFromReference('supply_energy_friction')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Energiebedarf: reference only -->
    <div v-if="reference" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">
          mdi-chart-bar
        </v-icon>
        <span>Energiebedarf</span>
      </div>

      <!-- Energy Consumption -->
      <v-text-field
        v-model.number="form.energyConsumption"
        type="number"
        :error-messages="validationErrors?.energy_consumption"
        label="Energiebedarf"
        suffix="kWh/a"
        :hint="hint.energy_consumption"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip
            v-if="hint.energy_consumption"
            location="bottom"
            color="surface" 
            content-class="tooltip"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.energy_consumption }}</span>
          </v-tooltip>
        </template>
      </v-text-field>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import api from '@/plugins/axios'
import { useBaseForm } from '@/composables/useBaseForm'

/* Props (match action.vue contract) */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

/* Field mapping (frontend -> backend keys) */
const fields = {
  actionId:               'action_id',
  fuel:                   'fuel',
  energyConsumption:      'energy_consumption',
  mainEffortFactor:       'main_effort_factor',
  passiveGeneratedEnergy: 'passive_generated_energy',
  passiveEffortFactor:    'passive_effort_factor',
  supplyEnergyFriction:   'supply_energy_friction',
}

/* useBaseForm */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {
    supplyEnergyFriction: 0,
    passiveEffortFactor: 0,
  },
  reference: computed(() => props.reference),
  planned:   computed(() => props.planned),
  shared:    computed(() => false), // not a shared form
  otherData: computed(() => props.otherData),
  sharedData:computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/* Local lists */
const energies = ref([])
const fuels = ref([])

/* Data fetchers (updated to /methods/freegenerator/*) */
async function fetchEnergies() {
  try {
    const { data } = await api.get('/methods/freegenerator/energies/')
    energies.value = [{ id: null, name: 'keine' }, ...data]
  } catch {}
}

async function fetchFuels() {
  try {
    const { data } = await api.get('/methods/freegenerator/fuels/')
    fuels.value = data
  } catch {}
}

/* Reset dependent fields */
watch(() => form.passiveGeneratedEnergy, value => {
  if (!value) form.passiveEffortFactor = 0
})

/* Expose validate so parent can call it on save error */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})


/* Lifecycle */
onMounted(async () => {
  await Promise.all([fetchEnergies(), fetchFuels()])
  await fetchHints()
})
</script>

<style scoped lang="scss">
.free-generator-form {
  width: 100%;
}

/* Input fields */
.free-generator-form :deep(.v-field) {
  border-radius: 18px;
}

.free-generator-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

/* Nested fields */
.free-generator-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Help and reference-copy actions */
.free-generator-form :deep(.v-input__append),
.free-generator-form :deep(.v-field__append-inner) {
  align-items: center;
}

.free-generator-form :deep(.v-input__append .v-icon),
.free-generator-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.free-generator-form :deep(.v-input__append .v-btn),
.free-generator-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.free-generator-form :deep(.v-input__append .v-btn:hover),
.free-generator-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled states */
.free-generator-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

/* Responsive */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .free-generator-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>